<script setup>
import { computed, ref, watch } from "vue";
import {
  defaultStages,
  PIPELINE_ATTRIBUTE_KEY,
  STAGE_COLORS,
  stageId,
} from "../helpers";

const props = defineProps({
  board: { type: Object, default: null },
  inboxes: { type: Array, default: () => [] },
  counts: { type: Object, default: () => ({}) },
  busy: { type: Boolean, default: false },
});

const emit = defineEmits(["close", "save", "delete"]);

const name = ref("");
const description = ref("");
const inboxIds = ref([]);
const compact = ref(false);
const stages = ref([]);
const error = ref("");
const colorLabels = {
  blue: "Azul",
  amber: "Amarelo",
  violet: "Violeta",
  teal: "Turquesa",
  green: "Verde",
  ruby: "Vermelho",
  slate: "Cinza",
};

const isCreating = computed(() => !props.board);
const canDelete = computed(
  () => props.board && props.board.key !== PIPELINE_ATTRIBUTE_KEY,
);

const reset = () => {
  name.value = props.board?.name || "Novo funil";
  description.value = props.board?.metadata?.description || "";
  inboxIds.value = [...(props.board?.metadata?.inboxIds || [])];
  compact.value = Boolean(props.board?.metadata?.compact);
  stages.value = (props.board?.metadata?.stages || defaultStages()).map(
    (stage, index) => ({
      ...stage,
      id: stage.id || stageId(stage.name, index),
      originalName: stage.name,
    }),
  );
  error.value = "";
};

watch(() => props.board, reset, { immediate: true });

const addStage = () => {
  const index = stages.value.length;
  stages.value.push({
    id: `nova_etapa_${Date.now()}`,
    name: `Nova etapa ${index + 1}`,
    originalName: null,
    color: STAGE_COLORS[index % STAGE_COLORS.length],
    type: "open",
    description: "",
  });
};

const moveStage = (index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= stages.value.length) return;
  const next = [...stages.value];
  [next[index], next[target]] = [next[target], next[index]];
  stages.value = next;
};

const removeStage = (index) => {
  const stage = stages.value[index];
  if (stage.originalName && props.counts[stage.originalName]) return;
  stages.value.splice(index, 1);
};

const toggleInbox = (inboxId) => {
  const numericId = Number(inboxId);
  inboxIds.value = inboxIds.value.includes(numericId)
    ? inboxIds.value.filter((id) => id !== numericId)
    : [...inboxIds.value, numericId];
};

const submit = () => {
  const normalizedName = name.value.trim();
  const normalizedStages = stages.value.map((stage) => ({
    ...stage,
    name: stage.name.trim(),
    description: stage.description?.trim() || "",
  }));
  const stageNames = normalizedStages.map((stage) => stage.name.toLowerCase());

  if (!normalizedName) {
    error.value = "Informe um nome para o funil.";
    return;
  }
  if (normalizedStages.length < 2) {
    error.value = "O funil precisa ter pelo menos duas etapas.";
    return;
  }
  if (normalizedStages.some((stage) => !stage.name)) {
    error.value = "Nenhuma etapa pode ficar sem nome.";
    return;
  }
  if (new Set(stageNames).size !== stageNames.length) {
    error.value = "Não use nomes de etapas repetidos.";
    return;
  }

  error.value = "";
  emit("save", {
    name: normalizedName,
    metadata: {
      description: description.value.trim(),
      inboxIds: inboxIds.value,
      compact: compact.value,
      stages: normalizedStages,
    },
  });
};

const requestDelete = () => {
  if (!canDelete.value) return;
  if (
    window.confirm(
      `Excluir o funil “${props.board.name}”? Os dados das conversas não serão apagados.`,
    )
  ) {
    emit("delete", props.board);
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-6"
    @click.self="emit('close')"
  >
    <section
      class="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-n-weak bg-n-background shadow-xl"
    >
      <header
        class="flex items-center justify-between border-b border-n-weak px-5 py-4"
      >
        <div>
          <h2 class="text-base font-semibold">
            {{ isCreating ? "Criar funil" : "Configurar funil" }}
          </h2>
          <p class="mt-0.5 text-sm text-n-slate-10">
            Configuração compartilhada automaticamente com toda a equipe.
          </p>
        </div>
        <button
          type="button"
          class="rounded-lg p-2 hover:bg-n-alpha-2"
          aria-label="Fechar"
          @click="emit('close')"
        >
          <i class="i-lucide-x size-5" />
        </button>
      </header>

      <div class="flex-1 space-y-6 overflow-y-auto px-5 py-5">
        <div class="grid gap-4 sm:grid-cols-2">
          <label class="space-y-1.5 text-sm font-medium">
            <span>Nome do funil</span>
            <input
              v-model="name"
              class="h-10 w-full rounded-lg border border-n-weak bg-n-background px-3 font-normal outline-none focus:border-n-brand"
            />
          </label>
          <label class="space-y-1.5 text-sm font-medium">
            <span>Descrição</span>
            <input
              v-model="description"
              placeholder="Ex.: vendas recebidas pelo WhatsApp"
              class="h-10 w-full rounded-lg border border-n-weak bg-n-background px-3 font-normal outline-none focus:border-n-brand"
            />
          </label>
        </div>

        <div>
          <div class="flex items-center justify-between gap-3">
            <div>
              <h3 class="text-sm font-semibold">Etapas</h3>
              <p class="text-xs text-n-slate-10">
                Reordene, personalize e marque resultados.
              </p>
            </div>
            <button
              type="button"
              class="inline-flex h-8 items-center gap-1.5 rounded-lg border border-n-weak px-3 text-sm font-medium hover:bg-n-alpha-2"
              @click="addStage"
            >
              <i class="i-lucide-plus size-4" /> Nova etapa
            </button>
          </div>

          <div class="mt-3 space-y-2">
            <div
              v-for="(stage, index) in stages"
              :key="stage.id"
              class="grid items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-2 sm:grid-cols-[auto_minmax(150px,1fr)_110px_120px_auto]"
            >
              <div class="flex items-center">
                <button
                  type="button"
                  class="rounded p-1 hover:bg-n-alpha-2 disabled:opacity-30"
                  :disabled="index === 0"
                  title="Mover para cima"
                  @click="moveStage(index, -1)"
                >
                  <i class="i-lucide-chevron-up size-4" />
                </button>
                <button
                  type="button"
                  class="rounded p-1 hover:bg-n-alpha-2 disabled:opacity-30"
                  :disabled="index === stages.length - 1"
                  title="Mover para baixo"
                  @click="moveStage(index, 1)"
                >
                  <i class="i-lucide-chevron-down size-4" />
                </button>
              </div>
              <input
                v-model="stage.name"
                aria-label="Nome da etapa"
                class="h-9 min-w-0 rounded-md border border-n-weak bg-n-background px-2 text-sm outline-none focus:border-n-brand"
              />
              <select
                v-model="stage.color"
                aria-label="Cor"
                class="h-9 rounded-md border border-n-weak bg-n-background px-2 text-sm"
              >
                <option
                  v-for="color in STAGE_COLORS"
                  :key="color"
                  :value="color"
                >
                  {{ colorLabels[color] }}
                </option>
              </select>
              <select
                v-model="stage.type"
                aria-label="Tipo da etapa"
                class="h-9 rounded-md border border-n-weak bg-n-background px-2 text-sm"
              >
                <option value="open">Em andamento</option>
                <option value="won">Ganho</option>
                <option value="lost">Perdido</option>
              </select>
              <button
                type="button"
                class="rounded p-2 text-n-ruby-10 hover:bg-n-ruby-3 disabled:cursor-not-allowed disabled:opacity-30"
                :disabled="
                  stages.length <= 2 ||
                  Boolean(stage.originalName && counts[stage.originalName])
                "
                :title="
                  stage.originalName && counts[stage.originalName]
                    ? 'Mova os cards antes de excluir esta etapa'
                    : 'Excluir etapa'
                "
                @click="removeStage(index)"
              >
                <i class="i-lucide-trash-2 size-4" />
              </button>
              <input
                v-model="stage.description"
                aria-label="Orientação da etapa"
                placeholder="Orientação para equipe e automações (opcional)"
                class="h-8 min-w-0 rounded-md border border-n-weak bg-n-background px-2 text-xs outline-none focus:border-n-brand sm:col-span-3 sm:col-start-2"
              />
            </div>
          </div>
        </div>

        <div>
          <h3 class="text-sm font-semibold">Caixas de entrada</h3>
          <p class="text-xs text-n-slate-10">
            Sem seleção, o funil inclui todas as caixas.
          </p>
          <div class="mt-3 grid gap-2 sm:grid-cols-2">
            <label
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm hover:bg-n-alpha-1"
            >
              <input
                type="checkbox"
                :checked="inboxIds.includes(Number(inbox.id))"
                @change="toggleInbox(inbox.id)"
              />
              <span class="truncate">{{ inbox.name }}</span>
            </label>
          </div>
        </div>

        <label
          class="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-n-weak p-3"
        >
          <span>
            <strong class="block text-sm font-medium">Visual compacto</strong>
            <span class="text-xs text-n-slate-10"
              >Mostra menos detalhes e mais cards por coluna.</span
            >
          </span>
          <input v-model="compact" type="checkbox" />
        </label>

        <p
          v-if="error"
          class="rounded-lg bg-n-ruby-3 px-3 py-2 text-sm text-n-ruby-11"
        >
          {{ error }}
        </p>
      </div>

      <footer
        class="flex items-center justify-between gap-3 border-t border-n-weak px-5 py-4"
      >
        <button
          v-if="canDelete"
          type="button"
          class="text-sm font-medium text-n-ruby-10 hover:underline"
          :disabled="busy"
          @click="requestDelete"
        >
          Excluir funil
        </button>
        <span v-else />
        <div class="flex gap-2">
          <button
            type="button"
            class="h-9 rounded-lg border border-n-weak px-4 text-sm font-medium hover:bg-n-alpha-2"
            :disabled="busy"
            @click="emit('close')"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white disabled:opacity-60"
            :disabled="busy"
            @click="submit"
          >
            <i v-if="busy" class="i-lucide-loader-circle size-4 animate-spin" />
            {{ isCreating ? "Criar funil" : "Salvar alterações" }}
          </button>
        </div>
      </footer>
    </section>
  </div>
</template>
